import { useState, useCallback } from 'react';
import axios from 'axios';
import { supabase } from '../config/supabase';
import {
  PrivateLeaderboardGroup,
  PrivateLeaderboardRanking,
  CreateLeaderboardRequest,
  CreateLeaderboardResponse,
  JoinLeaderboardResponse,
  LeaveLeaderboardResponse,
  GetUserLeaderboardGroupsResponse,
  GetPrivateLeaderboardRankingsResponse
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface UsePrivateLeaderboardsReturn {
  // State
  userGroups: PrivateLeaderboardGroup[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchUserGroups: () => Promise<void>;
  createLeaderboard: (request: CreateLeaderboardRequest) => Promise<CreateLeaderboardResponse>;
  joinLeaderboard: (inviteCode: string) => Promise<JoinLeaderboardResponse>;
  leaveLeaderboard: (groupId: string) => Promise<LeaveLeaderboardResponse>;
  getGroupRankings: (groupId: string, category: string, gameDate?: string) => Promise<PrivateLeaderboardRanking[]>;
  getInviteLink: (groupId: string) => Promise<{ invite_code: string; invite_link: string } | null>;
}

/**
 * Hook for managing private leaderboards
 */
export const usePrivateLeaderboards = (): UsePrivateLeaderboardsReturn => {
  const [userGroups, setUserGroups] = useState<PrivateLeaderboardGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractErrorMessage = useCallback((err: unknown, fallback: string): string => {
    if (axios.isAxiosError(err)) {
      const responseData = err.response?.data as any;

      if (responseData) {
        if (typeof responseData === 'string') {
          return responseData;
        }

        if (typeof responseData.error === 'string') {
          return responseData.error;
        }

        if (responseData.error && typeof responseData.error === 'object' && typeof responseData.error.message === 'string') {
          return responseData.error.message;
        }

        if (typeof responseData.message === 'string') {
          return responseData.message;
        }
      }

      return err.message || fallback;
    }

    if (err instanceof Error) {
      return err.message;
    }

    return fallback;
  }, []);

  /**
   * Get Supabase auth token for API requests
   */
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  /**
   * Fetch all leaderboard groups the user is a member of
   */
  const fetchUserGroups = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.get<GetUserLeaderboardGroupsResponse>(
        `${API_BASE_URL}/api/leaderboard/private/my-groups`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setUserGroups(response.data.groups);
        console.debug('[usePrivateLeaderboards] Loaded private groups', response.data.groups);
      } else {
        throw new Error('Failed to fetch user groups');
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to fetch user groups');
      setError(errorMessage);
      console.error('Error fetching user groups:', err);
    } finally {
      setLoading(false);
    }
  }, [extractErrorMessage, getAuthToken]);

  /**
   * Create a new private leaderboard
   */
  const createLeaderboard = useCallback(async (
    request: CreateLeaderboardRequest
  ): Promise<CreateLeaderboardResponse> => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.post<CreateLeaderboardResponse>(
        `${API_BASE_URL}/api/leaderboard/private/create`,
        request,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Refresh user groups after successful creation
      if (response.data.success) {
        await fetchUserGroups();
      }

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to create leaderboard');

      setError(errorMessage);
      console.error('Error creating leaderboard:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [extractErrorMessage, getAuthToken, fetchUserGroups]);

  /**
   * Join a private leaderboard via invite code
   */
  const joinLeaderboard = useCallback(async (
    inviteCode: string
  ): Promise<JoinLeaderboardResponse> => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.post<JoinLeaderboardResponse>(
        `${API_BASE_URL}/api/leaderboard/private/join`,
        { invite_code: inviteCode.toUpperCase() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Refresh user groups after successful join
      if (response.data.success) {
        await fetchUserGroups();
      }

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to join leaderboard');

      setError(errorMessage);
      console.error('Error joining leaderboard:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [extractErrorMessage, getAuthToken, fetchUserGroups]);

  /**
   * Leave a private leaderboard
   */
  const leaveLeaderboard = useCallback(async (
    groupId: string
  ): Promise<LeaveLeaderboardResponse> => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.delete<LeaveLeaderboardResponse>(
        `${API_BASE_URL}/api/leaderboard/private/${groupId}/leave`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Refresh user groups after successful leave
      if (response.data.success) {
        await fetchUserGroups();
      }

      return response.data;
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Failed to leave leaderboard');

      setError(errorMessage);
      console.error('Error leaving leaderboard:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [extractErrorMessage, getAuthToken, fetchUserGroups]);

  /**
   * Get rankings for a specific private leaderboard group
   */
  const getGroupRankings = useCallback(async (
    groupId: string,
    category: string,
    gameDate?: string
  ): Promise<PrivateLeaderboardRanking[]> => {
    try {
      // Validate that this group tracks the requested category
      const group = userGroups.find(g => g.group_id === groupId);
      if (group) {
        const trackedCategories = Array.isArray(group.tracked_categories)
          ? group.tracked_categories
          : [];

        if (!trackedCategories.includes(category)) {
          console.warn(`Group ${groupId} does not track category ${category}`);
          return [];
        }
      }

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams({
        category,
        ...(gameDate && { game_date: gameDate })
      });

      const response = await axios.get<GetPrivateLeaderboardRankingsResponse>(
        `${API_BASE_URL}/api/leaderboard/private/${groupId}/rankings?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        return response.data.rankings;
      }

      return [];
    } catch (err) {
      console.error('Error fetching group rankings:', err);
      return [];
    }
  }, [getAuthToken, userGroups]);

  /**
   * Get invite link for a private leaderboard
   */
  const getInviteLink = useCallback(async (
    groupId: string
  ): Promise<{ invite_code: string; invite_link: string } | null> => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.get<{ success: boolean; invite_code?: string; invite_link?: string }>(
        `${API_BASE_URL}/api/leaderboard/private/${groupId}/invite-link`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.invite_code && response.data.invite_link) {
        return {
          invite_code: response.data.invite_code,
          invite_link: response.data.invite_link
        };
      }

      return null;
    } catch (err) {
      console.error('Error fetching invite link:', err);
      return null;
    }
  }, [getAuthToken]);

  return {
    userGroups,
    loading,
    error,
    fetchUserGroups,
    createLeaderboard,
    joinLeaderboard,
    leaveLeaderboard,
    getGroupRankings,
    getInviteLink
  };
};
